<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { api } from '../../api/client';
  import type { LocationChoice, PersonEntry, SharingDefaults } from '../../api/types';
  import PageHeader from '../PageHeader.svelte';
  import { t } from '../../i18n/index.ts';

  // SharingSections are the account page's sharing: everyone the member
  // shares a group with, each opening what they see (spec 001 FR-079),
  // and what new groups start with (001 FR-072). locked shows the defaults
  // read-only for a child, whose parents change them, while each person
  // still opens.
  let { locked = false }: { locked?: boolean } = $props();
  let people = $state<PersonEntry[]>([]);
  let defaults = $state<SharingDefaults | null>(null);
  let error = $state('');
  let saved = $state(false);
  let savedTimer: ReturnType<typeof setTimeout> | undefined;
  // reverts redraws the controls after a failed save: reading back the same
  // values changes nothing Svelte would write, so a control would keep the
  // choice that failed.
  let reverts = $state(0);

  api.get<PersonEntry[]>('/me/people').then((p) => (people = p)).catch((e) => (error = e.message));
  api.get<SharingDefaults>('/me/sharing-defaults').then((d) => (defaults = d)).catch((e) => (error = e.message));

  const levels: { value: LocationChoice; label: string }[] = [
    { value: 'detailed', label: t('componentsSharingDetailed') },
    { value: 'standard', label: t('componentsSharingStandard') },
    { value: 'choose', label: t('componentsSharingWhenIChoose') },
    { value: 'off', label: t('appOff') },
  ];
  const switches: { key: 'activities' | 'heart_rate' | 'health' | 'rest_sleep' | 'driving' | 'phone' | 'email'; name: string }[] = [
    { key: 'activities', name: t('componentsSharingActivities') },
    { key: 'heart_rate', name: t('componentsSharingHeartRate') },
    { key: 'health', name: t('componentsSharingHealth') },
    { key: 'rest_sleep', name: t('componentsSharingRestSleep') },
    { key: 'driving', name: t('componentsSharingDriving') },
    { key: 'phone', name: t('componentsSharingPhone') },
    { key: 'email', name: t('componentsSharingEmail') },
  ];
  const lookbacks = [
    { days: 1, label: t('componentsSharingTodayYesterday') },
    { days: 7, label: t('componentsSharingWeek') },
    { days: 30, label: t('componentsSharingMonth') },
    { days: 90, label: t('componentsSharingThreeMonths') },
    { days: 365, label: t('componentsSharingYear') },
    { days: 0, label: t('componentsSharingForever') },
  ];
  let custom = $state(false);
  $effect(() => {
    if (defaults && !lookbacks.some((l) => l.days === defaults!.lookback_days)) custom = true;
  });

  // save sends the whole set, as the server takes it, and says it saved or
  // why not (spec 001 FR-065).
  async function save(change: Partial<SharingDefaults>) {
    if (!defaults) return;
    clearTimeout(savedTimer);
    saved = false;
    error = '';
    const { set: _, ...current } = defaults;
    try {
      defaults = await api.put<SharingDefaults>('/me/sharing-defaults', { ...current, ...change });
      saved = true;
      savedTimer = setTimeout(() => (saved = false), 2000);
    } catch (e: any) {
      error = e.message;
      defaults = await api.get<SharingDefaults>('/me/sharing-defaults').catch(() => defaults);
      custom = !!defaults && !lookbacks.some((l) => l.days === defaults!.lookback_days);
      reverts++;
    }
  }
</script>

<div class="card p-5 mb-6">
  <PageHeader title={t('componentsSharingPeopleTitle')} level="section" />
  <p class="text-sm text-text-muted mb-3">{t('componentsSharingPeopleIntro')}</p>
  {#if people.length === 0}
    <p class="text-sm text-text-muted">{t('componentsSharingNoOne')}</p>
  {:else}
    <ul class="divide-y" style="border-color: var(--theme-surface1);">
      {#each people as p (p.user_id)}
        <li>
          <button class="w-full text-left py-2 flex items-center gap-3 hover:opacity-80" onclick={() => navigate(`/people/${p.user_id}`)}>
            <span class="flex-1">
              <span class="block font-medium">{p.display_name}</span>
              <span class="block text-xs text-text-muted">{p.groups.join(', ')}</span>
            </span>
            <i aria-hidden="true" class="las la-angle-right"></i>
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

{#if defaults}
  <fieldset disabled={locked} class="card p-5 mb-6 min-w-0">
    <PageHeader title={t('componentsSharingDefaultsTitle')} level="section">
      <span class="text-sm text-text-muted" role="status">{saved ? t('componentsSharingSaved') : ''}</span>
    </PageHeader>
    <p class="text-sm text-text-muted mb-3">{t('componentsSharingDefaultsIntro')}</p>
    {#if error}<p class="text-sm mb-2" style="color: var(--theme-error);">{error}</p>{/if}
    {#key reverts}
    <div class="flex flex-wrap items-center gap-3 mb-3">
      <label for="default-location" class="text-sm font-medium w-24">{t('componentsSharingLocation')}</label>
      <select id="default-location" class="input" value={defaults.location}
              onchange={(e) => save({ location: (e.target as HTMLSelectElement).value as LocationChoice })}>
        {#each levels as l (l.value)}<option value={l.value}>{l.label}</option>{/each}
      </select>
    </div>
    <div class="flex flex-wrap items-center gap-3 mb-3">
      <label for="default-lookback" class="text-sm font-medium w-24">{t('componentsSharingHistory')}</label>
      <select id="default-lookback" class="input" value={custom ? 'custom' : String(defaults.lookback_days)}
              onchange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                custom = v === 'custom';
                if (!custom) save({ lookback_days: Number(v) });
              }}>
        {#each lookbacks as l (l.days)}<option value={String(l.days)}>{l.label}</option>{/each}
        <option value="custom">{t('componentsSharingCustom')}</option>
      </select>
      {#if custom}
        <input type="number" min="1" max="36500" class="input w-24" aria-label={t('componentsSharingDaysBackLabel')} value={defaults.lookback_days || ''}
               onchange={(e) => { const n = Number((e.target as HTMLInputElement).value); if (Number.isInteger(n) && n > 0) save({ lookback_days: n }); }} />
        <span class="text-sm text-text-muted">{t('componentsSharingDaysBack')}</span>
      {/if}
    </div>
    <div class="grid sm:grid-cols-2 gap-2">
      {#each switches as s (s.key)}
        <label class="text-sm flex items-center gap-2">
          <input type="checkbox" checked={defaults[s.key]} onchange={(e) => save({ [s.key]: (e.target as HTMLInputElement).checked })} />
          {s.name}
        </label>
      {/each}
    </div>
    {/key}
  </fieldset>
{/if}
